import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { t } from 'i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FiFilter } from 'react-icons/fi';
import { RadixButton, RadixDialog, RadixInput, RadixSpinner } from '@/components/radix';
import { useDebounce, useMediaQuery } from '@/hooks/shared';
import { useFetchDoctorsList } from '@/hooks/api/doctorPortal';
import { api } from '@/utils/Api/POS';
import type {
    GetApiDoctorsPortalDoctors200DoctorsItem,
    GetApiDoctorsPortalMessages200,
    GetApiDoctorsPortalMessages200MessagesItem,
} from '@/shared/api/models';
import type { GetApiDoctorsPortalMessagesPriority } from '@/shared/api/models/getApiDoctorsPortalMessagesPriority';
import { GetApiDoctorsPortalDoctorsStatus } from '@/shared/api/models/getApiDoctorsPortalDoctorsStatus';
import { GetApiDoctorsPortalMessagesSortBy } from '@/shared/api/models/getApiDoctorsPortalMessagesSortBy';
import { GetApiDoctorsPortalMessagesSortOrder } from '@/shared/api/models/getApiDoctorsPortalMessagesSortOrder';
import { useSelector } from 'react-redux';
import type { RootState } from '@/redux/slices/slices.types';
import { toast } from 'sonner';
import ChevronRightIcon from '@/assets/Marketing/ChevronRight.svg';
import CardLayout from './components/CardLayout';
import TableLayout from './components/TableLayout';
import MessagesFilterModal, { type MessagesViewState } from './components/MessagesFilterModal';

const LINKED_DOCTORS_LIMIT = 100;
const PAGE_SIZE = 10;
const DOCTOR_ALL_VALUE = '__all__';

const defaultViewState: MessagesViewState = {
    doctorId: DOCTOR_ALL_VALUE,
    viewMode: 'table',
    sortBy: GetApiDoctorsPortalMessagesSortBy.isPinned,
    sortOrder: GetApiDoctorsPortalMessagesSortOrder.desc,
};

export default function DoctorMessages() {
    const location = useLocation();
    const stateDoctorUuid = (location.state as { doctorUuid?: string } | undefined)?.doctorUuid;
    const [viewState, setViewState] = useState<MessagesViewState>(() => ({
        ...defaultViewState,
        doctorId: stateDoctorUuid && typeof stateDoctorUuid === 'string' ? stateDoctorUuid : defaultViewState.doctorId,
    }));
    const [page, setPage] = useState<number>(1);
    const [searchInput, setSearchInput] = useState('');
    const debouncedKeyword = useDebounce(searchInput, 400);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [previewMessage, setPreviewMessage] = useState<GetApiDoctorsPortalMessages200MessagesItem | null>(null);
    const isDesktop = useMediaQuery('(min-width: 960px)');
    const isMd = useMediaQuery('(min-width: 768px)');

    useEffect(() => {
        setPage(1);
    }, [debouncedKeyword]);

    useEffect(() => {
        if (stateDoctorUuid && typeof stateDoctorUuid === 'string') {
            setViewState((prev) => ({ ...prev, doctorId: stateDoctorUuid }));
        }
    }, [stateDoctorUuid]);

    const fetchParams = useMemo(
        () => ({
            page: 1,
            limit: LINKED_DOCTORS_LIMIT,
            status: GetApiDoctorsPortalDoctorsStatus.LINKED,
        }),
        [],
    );
    useFetchDoctorsList(fetchParams);
    const { doctors } = useSelector((state: RootState) => state.doctorsList);

    const doctorOptions = useMemo(() => {
        const list = (doctors || []) as GetApiDoctorsPortalDoctors200DoctorsItem[];
        const options = [{ value: DOCTOR_ALL_VALUE, label: t('Common.All') }];
        list.forEach((d) => {
            options.push({
                value: d.uuid,
                label: `${d.firstName} ${d.lastName}`.trim() || d.email,
            });
        });
        return options;
    }, [doctors]);

    const messagesQueryParams = useMemo(
        () => ({
            doctorUuid: viewState.doctorId === DOCTOR_ALL_VALUE ? undefined : viewState.doctorId,
            page,
            limit: PAGE_SIZE,
            sortBy: viewState.sortBy as GetApiDoctorsPortalMessagesSortBy | undefined,
            sortOrder: (viewState.sortOrder as GetApiDoctorsPortalMessagesSortOrder) || undefined,
            priority: viewState.priority as GetApiDoctorsPortalMessagesPriority | undefined,
            keyword: debouncedKeyword.trim() || undefined,
        }),
        [viewState.doctorId, viewState.sortBy, viewState.sortOrder, viewState.priority, debouncedKeyword, page],
    );

    const messagesQuery = useQuery<GetApiDoctorsPortalMessages200>({
        queryKey: ['doctors-portal-messages', messagesQueryParams],
        queryFn: () => api.getApiDoctorsPortalMessages(messagesQueryParams),
    });

    const markReadMutation = useMutation({
        mutationFn: async (ids: string[]) =>
            toast.promise(
                api.patchApiDoctorsPortalMessagesReadBulk({
                    messageIds: ids,
                }),
                {
                    loading: t('Doctors.MarkingMessagesRead'),
                    success: t('Doctors.MarkMessagesReadSuccess'),
                    error: t('Doctors.MarkMessagesReadFailed'),
                },
            ),
        onSuccess: () => {
            void messagesQuery.refetch();
        },
    });

    const markReadSilentMutation = useMutation({
        mutationFn: (ids: string[]) => api.patchApiDoctorsPortalMessagesReadBulk({ messageIds: ids }),
        onSuccess: () => {
            void messagesQuery.refetch();
        },
    });

    const messages = useMemo(() => messagesQuery.data?.messages ?? [], [messagesQuery.data?.messages]);

    const handleApplyFilter = useCallback((state: MessagesViewState) => {
        setViewState(state);
        setPage(1);
    }, []);

    /** Map table column id to API sortBy; used for server sorting. */
    const tableColumnToSortBy: Record<string, string> = useMemo(
        () => ({
            title: 'title',
            priority: 'priority',
            pinned: 'isPinned',
            validUntil: 'validUntil',
        }),
        [],
    );

    const handleTableSort = useCallback(
        (columnId: string) => {
            const apiSortBy = tableColumnToSortBy[columnId] ?? columnId;
            setViewState((prev) => ({
                ...prev,
                sortBy: apiSortBy,
                sortOrder: prev.sortBy === apiSortBy && prev.sortOrder === 'desc' ? 'asc' : 'desc',
            }));
            setPage(1);
        },
        [tableColumnToSortBy],
    );

    const handleMarkSingleRead = useCallback(
        (id: string) => {
            if (!id) return;
            markReadMutation.mutate([id]);
        },
        [markReadMutation],
    );

    const handleMarkAllOnPageRead = useCallback(() => {
        const unreadIds = messages.filter((m) => !m.readAt).map((m) => m.id);
        if (unreadIds.length === 0) return;
        markReadMutation.mutate(unreadIds);
    }, [messages, markReadMutation]);

    const openPreview = useCallback(
        (msg: GetApiDoctorsPortalMessages200MessagesItem) => {
            setPreviewMessage(msg);
            if (!msg.readAt) {
                markReadSilentMutation.mutate([msg.id]);
            }
        },
        [markReadSilentMutation],
    );

    const closePreview = useCallback(() => {
        setPreviewMessage(null);
    }, []);

    useEffect(() => {
        if (messagesQuery.isError) {
            toast.error(t('Doctors.FetchMessagesFailed'));
        }
    }, [messagesQuery.isError]);

    const hasUnreadOnPage = useMemo(() => messages.some((m) => !m.readAt), [messages]);

    const pagination = useMemo(() => {
        const currentPage = messagesQuery.data?.currentPage ?? page;
        const totalPages = messagesQuery.data?.totalPages ?? 0;
        const total = messagesQuery.data?.total ?? 0;
        return {
            currentPage,
            totalPages,
            total,
            hasNextPage: currentPage < totalPages,
            hasPreviousPage: currentPage > 1,
        };
    }, [messagesQuery.data?.currentPage, messagesQuery.data?.totalPages, messagesQuery.data?.total, page]);

    const paginationComponent = () => {
        if (!pagination || pagination.totalPages <= 1) return null;
        return (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-1 pt-4 pb-0 border-t border-border-default mt-4">
                <div className="text-sm text-text-secondary">
                    {t('Common.Showing')} {messages.length} {t('Common.of')} {pagination.total} {t('Common.items')}
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-text-secondary">
                        {t('Common.Page')} {pagination.currentPage} {t('Common.of')} {pagination.totalPages}
                    </span>
                    <RadixButton
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                        iconOnly
                        disabled={!pagination.hasPreviousPage || messagesQuery.isLoading}
                    >
                        <img src={ChevronRightIcon} alt="Previous" className="w-4 h-4 rotate-[180deg]" />
                    </RadixButton>
                    <RadixButton
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((prev) => prev + 1)}
                        iconOnly
                        disabled={!pagination.hasNextPage || messagesQuery.isLoading}
                    >
                        <img src={ChevronRightIcon} alt="Next" className="w-4 h-4" />
                    </RadixButton>
                </div>
            </div>
        );
    };

    return (
        <section className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-text-primary m-0 md:mb-1">
                        {t('Doctors.MessagesPageTitle')}
                    </h1>
                    <p className="text-sm text-text-secondary m-0">{t('Doctors.MessagesPageDescription')}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <RadixInput
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder={t('Doctors.FilterKeywordPlaceholder')}
                        className="w-full sm:max-w-[200px] min-w-0"
                    />
                    <RadixButton
                        variant="outline"
                        size="sm"
                        onClick={() => setFilterModalOpen(true)}
                        className="w-full sm:max-w-fit flex items-center justify-center gap-2"
                    >
                        <FiFilter className="w-4 h-4" />
                        {t('Doctors.FilterAndSort')}
                    </RadixButton>
                    {hasUnreadOnPage && (
                        <RadixButton
                            variant="outline"
                            size="sm"
                            className="w-full sm:max-w-fit"
                            onClick={handleMarkAllOnPageRead}
                            disabled={markReadMutation.isPending}
                        >
                            {markReadMutation.isPending ? t('Doctors.MarkingMessagesRead') : t('Calendar.MarkRead')}
                        </RadixButton>
                    )}
                </div>
            </div>

            {messagesQuery.isLoading && (
                <div className="flex items-center justify-center py-32 border border-border-default border-solid rounded-md">
                    <RadixSpinner size="md" />
                </div>
            )}

            {!messagesQuery.isLoading && messages.length === 0 && (
                <div className="text-center py-32 border border-border-default border-solid rounded-md">
                    <p className="text-text-secondary m-0">{t('Doctors.NoMessagesFound')}</p>
                </div>
            )}

            {!messagesQuery.isLoading && messages.length > 0 && (
                <>
                    {(!isMd || viewState.viewMode === 'card') && (
                        <CardLayout
                            messages={messages}
                            selectedDoctorId={viewState.doctorId}
                            openPreview={openPreview}
                            handleMarkSingleRead={handleMarkSingleRead}
                            markReadMutation={markReadMutation}
                        />
                    )}
                    {isMd && viewState.viewMode === 'table' && (
                        <TableLayout
                            messages={messages}
                            selectedDoctorId={viewState.doctorId}
                            openPreview={openPreview}
                            handleMarkSingleRead={handleMarkSingleRead}
                            markReadMutation={markReadMutation}
                            sortBy={viewState.sortBy}
                            sortOrder={viewState.sortOrder as 'asc' | 'desc' | undefined}
                            onSort={handleTableSort}
                        />
                    )}
                </>
            )}

            {paginationComponent()}

            <MessagesFilterModal
                open={filterModalOpen}
                onOpenChange={setFilterModalOpen}
                value={viewState}
                onApply={handleApplyFilter}
                doctorOptions={doctorOptions}
            />

            <RadixDialog
                open={!!previewMessage}
                onOpenChange={(open) => !open && closePreview()}
                title={previewMessage?.title ?? t('Doctors.MessagePreview')}
                position={isDesktop ? 'center' : 'bottom'}
                contentClassName="prose prose-sm max-w-none dark:prose-invert"
            >
                {previewMessage?.content && (
                    <div
                        className="message-html-content"
                        dangerouslySetInnerHTML={{ __html: previewMessage.content }}
                    />
                )}
            </RadixDialog>
        </section>
    );
}
